/**
 * /app/$tenantSlug/hiring/interviews — Manage → Hiring → Interviews: the
 * company-wide interview schedule (GET /hiring/interviews; HR sees every
 * round, a manager the rounds on the jobs they hire for).
 *
 * The interviewer's own list is Inbox → Interviews (/interviews). Anyone
 * without the schedule (an employee on an interview panel), and any old
 * "new interview" link that carries ?interview=<id>, goes there instead,
 * keeping ?interview= so the round still comes into view (the bell's link
 * is also remapped there in lib/notification-links).
 */

import { createFileRoute, Navigate } from "@tanstack/react-router";
import { z } from "zod";
import { HiringModuleShell } from "@/components/shared/hiring-module-shell";
import { HiringFeatureGate } from "@/components/hiring/hiring-feature-gate";
import { InterviewSchedule } from "@/components/hiring/interview-schedule";
import { useAuth } from "@/lib/auth";
import { isHRAdmin, MANAGER_PLUS } from "@/lib/nav";

const searchSchema = z.object({
  interview: z.coerce.number().int().positive().optional().catch(undefined),
});

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/hiring/interviews",
)({
  validateSearch: searchSchema,
  component: HiringInterviewsRoute,
});

function HiringInterviewsRoute() {
  const { interview } = Route.useSearch();
  const { tenantSlug } = Route.useParams();
  const { user } = useAuth();
  const hasSchedule = !!user?.role && MANAGER_PLUS.includes(user.role);
  if (!user) return null;
  if (interview != null || !hasSchedule) {
    return (
      <Navigate
        to="/app/$tenantSlug/interviews"
        params={{ tenantSlug }}
        search={interview != null ? { interview } : {}}
        replace
      />
    );
  }
  return (
    <HiringModuleShell
      title="Interviews"
      description={
        isHRAdmin(user.role)
          ? "Every interview round across your jobs. Once a round is over, record whether it happened."
          : "Interview rounds for the jobs you hire for. Once a round is over, record whether it happened."
      }
    >
      <HiringFeatureGate>
        <InterviewSchedule />
      </HiringFeatureGate>
    </HiringModuleShell>
  );
}
